<script>
import { required, email } from "vuelidate/lib/validators";

import {
  authMethods,
  authComputed,
  notificationMethods
} from "@/state/helpers";

import confirmButton from "../../../components/buttons/confirm-button.vue";
import TwoFactorSetupRequired from "./two-factor-setup-required.vue";
import ssoService from "@/services/sso.service";
import ssoConfig from "@/config/sso.config";
import defaultLogo from "@/assets/images/logo-dark.png";

export default {
  components: {
    confirmButton,
    TwoFactorSetupRequired
  },
  data() {
    return {
      // Basic login form
      email: "",
      password: "",
      submitted: false,
      tryingToLogIn: false,
      
      // 2FA form state
      showTwoFactorForm: false,
      showTwoFactorSetup: false,
      twoFactorForm: {
        code: "",
        recovery_code: ""
      },
      twoFactorMessage: "",
      setupMessage: "",
      isRequired: false,
      
      // SSO state
      ssoDetected: false,
      ssoProcessing: false,
      ssoMessage: "",
      
      // Loading and error states
      authError: null,
      isAuthError: false,
      currentSsoConfig: null,
      defaultLogo: defaultLogo,
    };
  },
  computed: {
    ...authComputed,
    notification() {
      return this.$store ? this.$store.state.notification : null;
    },
    // Check if we're in SSO context
    isSSO() {
      return !!this.ssoParams;
    },
    // Get redirect information for display
    redirectInfo() {
      if (this.ssoParams?.redirect_uri) {
        try {
          const url = new URL(this.ssoParams.redirect_uri);
          return {
            domain: url.hostname,
            app: this.ssoParams.app_origin || 'aplicación externa'
          };
        } catch {
          return null;
        }
      }
      return null;
    }
  },
  async created() {
    document.body.classList.add("auth-body-bg");
    
    // Detect SSO parameters on component creation
    await this.detectAndHandleSSO();
    this.currentSsoConfig = ssoConfig.clients.find(client => client.id === this.ssoParams?.client_id);
  },
  validations: {
    email: { required, email },
    password: { required },
    twoFactorForm: {
      code: {
        validCode(value) {
          // Code is valid if it's empty (using recovery code) or 6 digits
          return !value || /^\d{6}$/.test(value);
        }
      },
      recovery_code: {
        validRecoveryCode(value) {
          // Recovery code is valid if it's empty (using regular code) or 8 characters
          return !value || /^[A-Z0-9]{8}$/.test(value.toUpperCase());
        }
      }
    }
  },
  methods: {
    ...authMethods,
    ...notificationMethods,

    // SSO Detection and Handling
    async detectAndHandleSSO() {
      const ssoParams = ssoService.detectSSOParams();
      if (ssoParams) {
        this.ssoDetected = true;
        this.setSSOParams(ssoParams);
        // Validate SSO parameters
        const validation = ssoService.validateSSOParams(ssoParams);
        if (!validation.valid) {
          this.handleSSOError(new Error(validation.errors.join(', ')));
          return;
        }
        
        // Clean SSO params from URL for security
        // ssoService.cleanSSOParamsFromURL();
      }
    },

    

    handleSSOError(error) {
      console.error('SSO Error:', error);
      this.ssoMessage = "Error en la autenticación SSO. Puedes intentar el login manual.";
      this.error(error?.response?.data?.errors[0]?.title || error.message);
      
      // Use SSO service to handle error with potential redirect
      ssoService.handleSSOError(error, this.ssoParams);
    },

    // Login Methods
    async tryToLogIn() {
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.tryingToLogIn = true;
      this.authError = null;
      this.isAuthError = false;

      try {
        const result = await this.login({
          email: this.email,
          password: this.password,
          client_id: this.ssoParams?.client_id || null,
          redirect_uri: this.ssoParams?.redirect_uri || null,
          referer_url: this.ssoParams?.referer_url || null,
          state: this.ssoParams?.state || null
        });

        if (result.requires_2fa_setup) {
          this.handleTwoFactorSetupRequired(result);
          // Enable button for 2FA setup flow
          this.tryingToLogIn = false;
        } else if (result.requires_2fa) {
          this.handleTwoFactorRequired(result);
          // Enable button for 2FA verification flow
          this.tryingToLogIn = false;
        } else if (result.success) {
          // Keep tryingToLogIn = true during redirect
          this.handleLoginSuccess(result);
        }

      } catch (error) {
        this.error(error?.response?.data?.errors[0]?.title);
        this.authError = error || "";
        this.isAuthError = true;
        // Only enable button on error
        this.tryingToLogIn = false;
      }
    },

    // 2FA Handlers
    handleTwoFactorRequired(result) {
      this.showTwoFactorForm = true;
      this.twoFactorMessage = result.message || "Ingresa tu código de autenticación";
      this.resetTwoFactorForm();
    },

    handleTwoFactorSetupRequired(result) {
      this.showTwoFactorSetup = true;
      this.setupMessage = result.message || "Debes configurar la autenticación en dos pasos";
      this.isRequired = true;
      // Show the setup component directly in the login page
    },

    async verifyTwoFactor() {
      this.$v.twoFactorForm.$touch();
      
      // Validate that at least one field is filled
      const hasCode = this.twoFactorForm.code && this.twoFactorForm.code.length === 6;
      const hasRecoveryCode = this.twoFactorForm.recovery_code && this.twoFactorForm.recovery_code.length === 8;
      
      if (!hasCode && !hasRecoveryCode) {
        this.error("Debes ingresar un código de autenticación o un código de recuperación");
        return;
      }
      
      if (this.$v.twoFactorForm.$invalid) {
        return;
      }

      this.tryingToLogIn = true;

      try {
        const result = await this.verify2FA({
          code: this.twoFactorForm.code || null,
          recovery_code: this.twoFactorForm.recovery_code ? this.twoFactorForm.recovery_code.toUpperCase() : null,
          client_id: this.ssoParams?.client_id || null,
          redirect_uri: this.ssoParams?.redirect_uri || null,
          referer_url: this.ssoParams?.referer_url || null,
          state: this.ssoParams?.state || null
        });

        if (result.success) {
          // Keep tryingToLogIn = true during redirect
          this.handleLoginSuccess(result);
        }

      } catch (error) {
        if (error.response?.status === 429) {
          this.error('Demasiados intentos. Intenta de nuevo más tarde.');
        } else {
          this.error('Código inválido. Verifica tu aplicación autenticadora.');
        }
        // Only enable button on error
        this.tryingToLogIn = false;
      }
    },

    resetTwoFactorForm() {
      this.twoFactorForm = {
        code: "",
        recovery_code: ""
      };
      this.$v.twoFactorForm.$reset();
    },

    // Success Handler
    async handleLoginSuccess(result) {
      // Do NOT set tryingToLogIn to false - keep button disabled during redirect
      this.isAuthError = false;
      console.log(result);
      
      // Handle redirect logic
      if (result?.sso_redirect && result?.redirect_url) {
        // SSO redirect - keep loading state until redirect completes
        this.ssoMessage = `Redirigiendo a ${this.redirectInfo?.domain}...`;
        setTimeout(() => {
          ssoService.performRedirect(result?.redirect_url);
        }, 1500);
        
      } else {
        // Load permissions
        this.$store.dispatch('permission/loadList');
        // Normal redirect - keep loading state until navigation completes
        this.$router.push(
          this.$route.query.redirectFrom || { name: "home" }
        );
      }
    },

    // Utility Methods
    goBackToLogin() {
      this.showTwoFactorForm = false;
      this.showTwoFactorSetup = false;
      this.resetTwoFactorForm();
      this.clearTempData();
    },

    // Handle input restrictions
    onCodeInput(event) {
      // Only allow numbers and limit to 6 digits
      const value = event.target.value.replace(/\D/g, '').slice(0, 6);
      this.twoFactorForm.code = value;
      
      // Clear recovery code if user starts typing regular code
      if (value) {
        this.twoFactorForm.recovery_code = "";
      }
    },

    onRecoveryCodeInput(event) {
      // Only allow alphanumeric and limit to 8 characters
      const value = event.target.value.replace(/[^A-Z0-9]/gi, '').slice(0, 8).toUpperCase();
      this.twoFactorForm.recovery_code = value;
      
      // Clear regular code if user starts typing recovery code
      if (value) {
        this.twoFactorForm.code = "";
      }
    },

    // Handle 2FA setup completion
    onSetupCompleted(result) {
      // After successful 2FA setup, continue with login
      this.success('¡2FA configurado exitosamente! Ahora puedes acceder a tu cuenta.');
      
      // Activate loading state for redirect
      this.tryingToLogIn = true;
      
      // Hide setup form and continue to dashboard
      this.showTwoFactorSetup = false;
      this.handleLoginSuccess(result);
    },

    // Handle 2FA setup cancellation
    onSetupCancelled() {
      // User cancelled setup, go back to login form
      this.showTwoFactorSetup = false;
      this.clearTempData();
      
      // Reset form
      this.email = "";
      this.password = "";
      this.submitted = false;
    }
  }
};
</script>

<template>
  <div>
    <div class="home-btn d-none d-sm-block">
      <a href="/">
        <i class="mdi mdi-home-variant h2 text-white"></i>
      </a>
    </div>
    <div>
      <div class="container-fluid p-0">
        <div class="row no-gutters">
          <div class="col-lg-4">
            <div class="authentication-page-content p-4 d-flex align-items-center min-vh-100">
              <div class="w-100">
                <div class="row justify-content-center">
                  <div class="col-lg-9 max-h-100vh overflow-y-auto">
                    <div>
                      <div class="text-center">
                        <div>
                          <a href="/" class="logo">
                            <img :src="currentSsoConfig?.logo || defaultLogo" width="80" alt="logo" />
                          </a>
                        </div>

                        <h4 class="font-size-18 mt-4">Bienvenido!</h4>
                        <p class="text-muted">
                          <span v-if="!isSSO">Inicia sesión para continuar a Carné Joven Aragón.</span>
                          <span v-else>Autenticación para {{ currentSsoConfig?.name || 'aplicación externa' }}</span>
                        </p>
                        
                        <!-- SSO Indicator -->
                        <div v-if="ssoDetected" class="alert alert-info mt-3">
                          <i class="mdi mdi-link-variant mr-2"></i>
                          <span v-if="ssoProcessing">{{ ssoMessage }}</span>
                          <span v-else-if="redirectInfo">
                            Conectando con <strong>{{ redirectInfo.domain }}</strong>
                          </span>
                          <span v-else>Autenticación SSO detectada</span>
                        </div>
                      </div>

                      <b-alert
                        :variant="notification.type"
                        class="mt-3"
                        v-if="notification.message"
                        show
                      >{{ notification.message }}</b-alert>

                      <!-- Main Login Form -->
                      <div v-if="!showTwoFactorForm && !showTwoFactorSetup" class="p-2 mt-5">
                        <form class="form-horizontal" @submit.prevent="tryToLogIn">
                          <div class="form-group auth-form-group-custom mb-4">
                            <i class="ri-mail-line auti-custom-input-icon"></i>
                            <label for="email">Email</label>
                            <input
                              type="email"
                              v-model="email"
                              class="form-control"
                              id="email"
                              placeholder="Ingresa tu email"
                              :class="{ 'is-invalid': submitted && $v.email.$error }"
                            />
                            <div v-if="submitted && $v.email.$error" class="invalid-feedback">
                              <span v-if="!$v.email.required">Email es requerido.</span>
                              <span v-if="!$v.email.email">Email inválido.</span>
                            </div>
                          </div>

                          <div class="form-group auth-form-group-custom mb-4">
                            <i class="ri-lock-2-line auti-custom-input-icon"></i>
                            <label for="userpassword">Contraseña</label>
                            <input
                              v-model="password"
                              type="password"
                              class="form-control"
                              id="userpassword"
                              placeholder="Ingresa la contraseña"
                              :class="{ 'is-invalid': submitted && $v.password.$error }"
                            />
                            <div
                              v-if="submitted && !$v.password.required"
                              class="invalid-feedback"
                            >Contraseña es requerida.</div>
                          </div>

                          <div class="mt-4 text-center">
                            <confirmButton 
                              label="Entrar"
                              :loading="tryingToLogIn || ssoProcessing"
                            ></confirmButton>
                          </div>

                          <div class="mt-4 text-center">
                            <router-link tag="a" to="/forgot-password" class="text-muted">
                              <i class="mdi mdi-lock mr-1"></i> Olvidaste tu contraseña?
                            </router-link>
                          </div>
                        </form>
                      </div>

                      <!-- 2FA Verification Form -->
                      <div v-if="showTwoFactorForm" class="two-factor-form p-2 mt-5">
                        <div class="text-center mb-4">
                          <i class="ri-shield-check-line display-4 text-primary"></i>
                          <h4 class="mt-3">Verificación en dos pasos</h4>
                          <p class="text-muted">{{ twoFactorMessage }}</p>
                          <div v-if="isSSO" class="small text-info">
                            <i class="mdi mdi-link-variant mr-1"></i>
                            Después de la verificación serás redirigido a {{ redirectInfo?.domain }}
                          </div>
                        </div>

                        <form @submit.prevent="verifyTwoFactor">
                          <!-- Authenticator Code -->
                          <div class="form-group auth-form-group-custom mb-4">
                            <i class="ri-smartphone-line auti-custom-input-icon"></i>
                            <label for="twoFactorCode">Código del autenticador (6 dígitos)</label>
                            <input
                              type="text"
                              v-model="twoFactorForm.code"
                              @input="onCodeInput"
                              class="form-control text-center"
                              id="twoFactorCode"
                              placeholder="123456"
                              maxlength="6"
                              :class="{ 'is-invalid': $v.twoFactorForm.code.$error }"
                            />
                            <div v-if="$v.twoFactorForm.code.$error" class="invalid-feedback">
                              <span v-if="!$v.twoFactorForm.code.validCode">Debe ser un código de 6 dígitos.</span>
                            </div>
                          </div>

                          <div class="text-center mb-3">
                            <span class="text-muted">- O -</span>
                          </div>

                          <!-- Recovery Code -->
                          <div class="form-group auth-form-group-custom mb-4">
                            <i class="ri-key-line auti-custom-input-icon"></i>
                            <label for="recoveryCode">Código de recuperación (8 caracteres)</label>
                            <input
                              type="text"
                              v-model="twoFactorForm.recovery_code"
                              @input="onRecoveryCodeInput"
                              class="form-control text-center"
                              id="recoveryCode"
                              placeholder="ABC12345"
                              maxlength="8"
                              :class="{ 'is-invalid': $v.twoFactorForm.recovery_code.$error }"
                            />
                            <div v-if="$v.twoFactorForm.recovery_code.$error" class="invalid-feedback">
                              <span v-if="!$v.twoFactorForm.recovery_code.validRecoveryCode">Debe ser un código de 8 caracteres.</span>
                            </div>
                          </div>

                          <div class="mt-4 text-center">
                            <button 
                              type="submit" 
                              class="btn btn-primary btn-block waves-effect waves-light"
                              :disabled="tryingToLogIn"
                            >
                              <span v-if="tryingToLogIn">
                                <i class="mdi mdi-spin mdi-loading mr-1"></i>
                                Verificando...
                              </span>
                              <span v-else>
                                <i class="ri-shield-check-line mr-1"></i>
                                Verificar
                              </span>
                            </button>
                          </div>

                          <div class="mt-3 text-center">
                            <button 
                              type="button" 
                              @click="goBackToLogin"
                              class="btn btn-link text-muted"
                            >
                              <i class="ri-arrow-left-line mr-1"></i>
                              Volver al login
                            </button>
                          </div>
                        </form>
                      </div>

                                    <!-- 2FA Setup Required -->
              <div v-if="showTwoFactorSetup" class="p-2 mt-5">
                <TwoFactorSetupRequired 
                  @setup-completed="onSetupCompleted"
                  @cancel-setup="onSetupCancelled"
                />
              </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-8">
            <div class="authentication-bg">
              <div class="bg-overlay"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>